import { useSelector, useDispatch } from "react-redux";
import React, { useEffect, useCallback, memo, useState } from "react";
import { View, StyleSheet, TouchableOpacity, ScrollView } from "react-native";

// Custom Imports
import color from "@constants/color";
import t from "@constants/localization";
import Text from "@components/CustomText";
import { colors } from "@constants/theme";
import BackArrow from "@assets/svg/BackArrow";
import { normalize } from "@constants/metrics";
import vacationActions from "@actions/vacation.actions";
import ApplyLeaveIcon from "@assets/svg/Vacation/ApplyLeaveIcon";
import { vacationDataSelector } from "@selectors/vacation.selector";
import { VACATION_EVENTS } from "@constants/analyticsEventsCT";
import analytics from "@services/analytics";
import { scale, vh } from "@utils/scale";
import { fontFamily } from "@utils/styles";
import { getAllOrders } from "@services/api";
import CircularLoader from "../Story/CircularLoader";
import InitialVacationPolicy, {
  SeeRulesButton,
} from "./components/InitialVacationPolicy";
import PreviousVacation from "./components/PreviousVacation";
import UpcominngLeave from "./components/UpcominngLeave";

const PATH = "screens/Vacation/index";

const urlSelector = (state) => state.auth?.user?.url;

const Leave = ({ navigation, route }) => {
  const dispatch = useDispatch();
  const { data = {}, api = {} } = useSelector(vacationDataSelector);
  const url = useSelector(urlSelector);
  const { fetched = false, pending = false } = api || {};
  const {
    labels = {},
    paid_leaves = {},
    unpaid_leaves = {},
    past_paid_leaves = [],
    past_unpaid_leaves = [],
    terms_and_conditions = {},
    upcoming_cancelled_leaves = [],
    upcoming_leaves = [],
    vacation_title = "",
  } = data;
  const checkNewUser =
    upcoming_cancelled_leaves.length === 0 &&
    upcoming_leaves.length === 0 &&
    past_paid_leaves.length === 0 &&
    past_unpaid_leaves.length === 0;

  const [paidInfo, setPaidInfo] = useState(false);
  const [unpaidInfo, setUnpaidInfo] = useState(false);
  const [ordersList, setOrdersList] = useState([]);
  const [isFetchingOrders, setIsFetchingOrders] = useState(true);

  useEffect(() => {
    fetchLeaveDetails();
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    const payload = {
      offset: 0,
      limit: 10,
      url,
      userId: "",
      vacationMode: true,
    };
    getAllOrders(payload)
      .then(({ data }) => {
        setOrdersList(data.orders);
        setIsFetchingOrders(false);
      })
      .catch((e) => {
        console.log(e);
        setIsFetchingOrders(false);
      });
  };

  const fetchLeaveDetails = useCallback(() => {
    dispatch(vacationActions.getVacationDetails.pending());
  }, []);

  const goBackHandler = () => {
    navigation.goBack();
  };
  const onMarkVacation = async () => {
    onTouchStart();
    navigation.navigate("VacationCalendar");
    analytics.trackEvent(VACATION_EVENTS.LEAVE_SELECT);
  };

  const onTouchStart = () => {
    setTimeout(() => {
      paidInfo && setPaidInfo(false);
      unpaidInfo && setUnpaidInfo(false);
    }, 150);
  };

  return !fetched && pending ? (
    <View style={styles.loaderStyle}>
      <CircularLoader />
    </View>
  ) : (
    <View onTouchStart={onTouchStart} style={styles.container}>
      <View style={styles.topNavContainer}>
        <TouchableOpacity onPress={goBackHandler} style={styles.arrowCont}>
          <BackArrow />
        </TouchableOpacity>
        <Text variant="entityHeading" newfont={true} style={styles.headerTitle}>
          {vacation_title || t(`${PATH}/markLeave`)}
        </Text>
      </View>
      <ScrollView
        contentContainerStyle={styles.contentContainerStyle}
        style={styles.scrollView}
        showsVerticalScrollIndicator={false}
        showsHorizontalScrollIndicator={false}
      >
        {/* <PaidUnpaid
          infoText={labels?.limit_exceed}
          navigation={navigation}
          paid_leaves={paid_leaves}
          unpaid_leaves={unpaid_leaves}
          checkNewUser={checkNewUser}
          total_month_limit={labels?.total_month_limit}
          setPaidInfo={setPaidInfo}
          setUnpaidInfo={setUnpaidInfo}
          paidInfo={paidInfo}
          unpaidInfo={unpaidInfo}
          hideInfo={onTouchStart}
        /> */}
        {checkNewUser ? (
          <InitialVacationPolicy
            terms_and_conditions={terms_and_conditions}
            navigation={navigation}
          />
        ) : (
          <SeeRulesButton
            navigation={navigation}
            terms_and_conditions={terms_and_conditions}
          />
        )}
        {(upcoming_leaves.length !== 0 ||
          upcoming_cancelled_leaves.length !== 0) && (
          <UpcominngLeave
            upcoming_cancelled_leaves={upcoming_cancelled_leaves}
            upcoming_leaves={upcoming_leaves}
            navigation={navigation}
            labels={labels}
            hideInfo={onTouchStart}
          />
        )}
        {(past_paid_leaves.length !== 0 || past_unpaid_leaves.length !== 0) && (
          <PreviousVacation
            past_paid_leaves={past_paid_leaves}
            past_unpaid_leaves={past_unpaid_leaves}
            navigation={navigation}
          />
        )}
        {/* {Array.isArray(ordersList) && ordersList.length !== 0 ? (
          <HorizontalOrdersList
            fetchOrders={fetchOrders}
            ordersList={ordersList}
            isFetchingOrders={isFetchingOrders}
            navigation={navigation}
          />
        ) : null} */}
      </ScrollView>
      <View style={styles.buttonMainContainer}>
        <TouchableOpacity
          activeOpacity={0.8}
          style={styles.buttonContainer}
          onPress={onMarkVacation}
        >
          <ApplyLeaveIcon />
          <Text variant="title_18_semi" newfont={true} style={styles.button}>
            {t(`${PATH}/markLeave`)}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  contentContainerStyle: {
    flexGrow: 1,
    backgroundColor: color.white,
    paddingBottom: scale(100),
  },
  topNavContainer: {
    flexDirection: "row",
    paddingVertical: normalize(18),
    paddingHorizontal: normalize(11),
    backgroundColor: colors.white,
    alignItems: "center",
  },
  arrowCont: {
    marginRight: normalize(20),
  },
  infoText: {
    fontFamily: fontFamily.semiBold,
    fontSize: 14,
    fontWeight: "600",
    lineHeight: 22,
    textAlign: "left",
    width: "50%",
  },
  buttonContainer: {
    width: "92%",
    flexDirection: "row",
    height: 48,
    backgroundColor: color.primary,
    alignSelf: "center",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 6,
  },
  buttonMainContainer: {
    alignItems: "center",
    paddingVertical: 8,
    paddingHorizontal: 2,
    width: "100%",
    backgroundColor: color.white,
    position: "absolute",
    bottom: 0,
    elevation: 8,
    shadowColor: color.mediumBlack,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4.65,
    overflow: "hidden",
  },
  button: {
    color: color.white,
    marginStart: 13,
  },
  headerTitle: {
    marginBottom: 0,
    paddingHorizontal: 0,
  },
  loaderStyle: {
    position: "absolute",
    alignSelf: "center",
    zIndex: 100,
    marginTop: vh(150),
  },
});

export default memo(Leave);
